import { useState } from "react";
import { Mail, MapPin, ExternalLink, Instagram, Facebook, Linkedin } from "lucide-react";
import PageLayout from "../components/site/PageLayout";
import { SectionHeading } from "../components/site/SectionHeading";
import { Reveal } from "../components/site/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { submitForm } from "../lib/forms";
import { useContent } from "../lib/content";
import { toast } from "sonner";

const Contact = () => {
  const { content } = useContent();
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [partner, setPartner] = useState({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "", message: "" });
  const [busyC, setBusyC] = useState(false);
  const [busyP, setBusyP] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault(); setBusyC(true);
    try { await submitForm("contact", contact); toast.success("Message sent — we'll be in touch."); setContact({ name: "", email: "", subject: "", message: "" }); }
    catch { toast.error("Could not send. Try again."); } finally { setBusyC(false); }
  };
  const submitPartner = async (e) => {
    e.preventDefault(); setBusyP(true);
    try { await submitForm("partnership", partner); toast.success("Partnership inquiry sent!"); setPartner({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "", message: "" }); }
    catch { toast.error("Could not send. Try again."); } finally { setBusyP(false); }
  };

  return (
    <PageLayout title="Contact">
      <section className="relative pt-16 pb-10 overflow-hidden">
        <div className="ambient" aria-hidden />
        <div className="ambient-grid" aria-hidden />
        <div className="container-tt">
          <SectionHeading eyebrow="Contact" title="Say hello." description={content.contact.intro} />
        </div>
      </section>

      <section className="py-14">
        <div className="container-tt grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="card-surface p-8">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/70 border border-sky/15 text-skyDark"><Mail size={20} /></span>
                <div className="font-display text-2xl mt-5 text-stone">Email us</div>
                <a href={`mailto:${content.brand.email}`} className="link-underline text-skyDark font-medium mt-2 inline-block">{content.brand.email}</a>
                <p className="text-muted-foreground text-sm mt-3">Fastest way to reach the team. We usually respond within 48 hours.</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="card-surface p-8">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/70 border border-sky/15 text-skyDark"><MapPin size={20} /></span>
                <div className="font-display text-2xl mt-5 text-stone">Where we work</div>
                <ul className="mt-4 space-y-4">
                  {content.locations.map((l, i) => (
                    <li key={l.name} className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{l.name}</div>
                        <div className="text-sm text-muted-foreground">{l.address}</div>
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.mapQuery || l.name)}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`contact-directions-${i}`}
                        className="text-sm text-skyDark font-medium inline-flex items-center gap-1"
                      >
                        Directions <ExternalLink size={12} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card-surface p-8">
                <div className="font-display text-2xl text-stone">Follow along</div>
                <p className="text-muted-foreground text-sm mt-3">See workshop highlights, meet volunteers, and hear from seniors on our socials.</p>
                <div className="mt-5 flex flex-wrap gap-3" data-testid="contact-socials">
                  <a href={content.socials?.instagram || "#"} target="_blank" rel="noreferrer" data-testid="contact-social-instagram" className="btn-pill inline-flex items-center gap-2 h-12 px-5 border border-border bg-white text-stone hover:bg-skyDark hover:text-white hover:border-skyDark transition-colors">
                    <Instagram size={16} /> Instagram
                  </a>
                  <a href={content.socials?.facebook || "#"} target="_blank" rel="noreferrer" data-testid="contact-social-facebook" className="btn-pill inline-flex items-center gap-2 h-12 px-5 border border-border bg-white text-stone hover:bg-skyDark hover:text-white hover:border-skyDark transition-colors">
                    <Facebook size={16} /> Facebook
                  </a>
                  <a href={content.socials?.linkedin || "#"} target="_blank" rel="noreferrer" data-testid="contact-social-linkedin" className="btn-pill inline-flex items-center gap-2 h-12 px-5 border border-border bg-white text-stone hover:bg-skyDark hover:text-white hover:border-skyDark transition-colors">
                    <Linkedin size={16} /> LinkedIn
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7" id="partnership">
            <Reveal>
              <div className="card-surface p-6 md:p-10">
                <Tabs defaultValue="general" className="w-full">
                  <TabsList data-testid="contact-tabs" className="mb-6 bg-muted rounded-full h-12 p-1">
                    <TabsTrigger value="general" className="btn-pill h-10 px-5 data-[state=active]:bg-white data-[state=active]:text-skyDark data-[state=active]:shadow-card">General</TabsTrigger>
                    <TabsTrigger value="partner" className="btn-pill h-10 px-5 data-[state=active]:bg-white data-[state=active]:text-skyDark data-[state=active]:shadow-card">Partnership</TabsTrigger>
                  </TabsList>

                  <TabsContent value="general">
                    <form onSubmit={submitContact} className="grid gap-5" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div><Label>Name *</Label><Input required data-testid="c-name" className="h-12 mt-2 bg-white rounded-xl" value={contact.name} onChange={e => setContact({...contact, name: e.target.value})} /></div>
                        <div><Label>Email *</Label><Input required type="email" data-testid="c-email" className="h-12 mt-2 bg-white rounded-xl" value={contact.email} onChange={e => setContact({...contact, email: e.target.value})} /></div>
                      </div>
                      <div><Label>Subject</Label><Input data-testid="c-subject" className="h-12 mt-2 bg-white rounded-xl" value={contact.subject} onChange={e => setContact({...contact, subject: e.target.value})} /></div>
                      <div><Label>Message *</Label><Textarea required rows={5} data-testid="c-message" className="mt-2 bg-white rounded-xl" value={contact.message} onChange={e => setContact({...contact, message: e.target.value})} /></div>
                      <Button data-testid="c-submit" disabled={busyC} type="submit" className="btn-pill btn-motion btn-sky-fill h-12 text-white shadow-sky hover:shadow-skyHover border-0">{busyC ? "Sending…" : "Send message"}</Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="partner">
                    <form onSubmit={submitPartner} className="grid gap-5" data-testid="partner-form">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div><Label>Organization *</Label><Input required data-testid="p-org" className="h-12 mt-2 bg-white rounded-xl" value={partner.organization} onChange={e => setPartner({...partner, organization: e.target.value})} /></div>
                        <div><Label>Contact name *</Label><Input required data-testid="p-name" className="h-12 mt-2 bg-white rounded-xl" value={partner.contact_name} onChange={e => setPartner({...partner, contact_name: e.target.value})} /></div>
                        <div><Label>Email *</Label><Input required type="email" data-testid="p-email" className="h-12 mt-2 bg-white rounded-xl" value={partner.email} onChange={e => setPartner({...partner, email: e.target.value})} /></div>
                        <div><Label>Phone</Label><Input data-testid="p-phone" className="h-12 mt-2 bg-white rounded-xl" value={partner.phone} onChange={e => setPartner({...partner, phone: e.target.value})} /></div>
                      </div>
                      <div><Label>Type of partnership</Label><Input placeholder="Senior center visit, sponsorship, event…" data-testid="p-type" className="h-12 mt-2 bg-white rounded-xl" value={partner.partnership_type} onChange={e => setPartner({...partner, partnership_type: e.target.value})} /></div>
                      <div><Label>Message *</Label><Textarea required rows={5} data-testid="p-message" className="mt-2 bg-white rounded-xl" value={partner.message} onChange={e => setPartner({...partner, message: e.target.value})} /></div>
                      <Button data-testid="p-submit" disabled={busyP} type="submit" className="btn-pill btn-motion h-12 bg-stone hover:bg-sageDark text-white shadow-dark">{busyP ? "Sending…" : "Send partnership inquiry"}</Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Contact;
